import { useEffect } from 'react';
import { BookOpen, Globe2, Layers, Sparkles, type LucideIcon } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';

import type { InstituteClass } from '@/types/instituteClass';
import { CLASS_STAGES, stageOfClass, type ClassStage } from '../../utils';
import type { StructureFocus } from './ClassesStatRow';

/**
 * The same roster read the other way round — by stage, and by subject.
 *
 * The overview tab answers "what is Class 8". This one answers the two
 * questions a head of academics has instead: how the school is banded, and who
 * is actually teaching each subject.
 */

interface StageBand {
  stage: ClassStage;
  classes: InstituteClass[];
  sections: number;
  seats: number;
  students: number;
}

/** Every class folded into its stage. Stages nobody runs are left out. */
function bandsOf(rows: InstituteClass[]): StageBand[] {
  return CLASS_STAGES.flatMap((stage) => {
    const own = rows.filter((row) => stageOfClass(row) === stage);
    if (own.length === 0) return [];

    return [
      {
        stage,
        classes: own,
        sections: own.reduce((sum, row) => sum + row.sections.length, 0),
        seats: own.reduce((sum, row) => sum + row.totalCapacity, 0),
        students: own.reduce((sum, row) => sum + row.studentCount, 0),
      },
    ];
  });
}

/** How many classes teach each subject, most-taught first. */
function subjectSpread(rows: InstituteClass[], pick: (row: InstituteClass) => string[]): [string, number][] {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    pick(row).forEach((subject) => counts.set(subject, (counts.get(subject) ?? 0) + 1));
  });

  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

const STAGE_CONFIG: Record<
  string,
  {
    cardBg: string;
    dotClass: string;
    barClass: string;
  }
> = {
  'Pre-Primary': {
    cardBg: 'border-amber-100/90 dark:border-amber-950/60 bg-gradient-to-b from-[#fffdf5] to-white dark:from-[#1a1408] dark:to-[#111c2e]',
    dotClass: 'bg-amber-500',
    barClass: 'bg-gradient-to-r from-amber-400 to-amber-500',
  },
  Primary: {
    cardBg: 'border-indigo-100/90 dark:border-indigo-950/60 bg-gradient-to-b from-[#f8faff] to-white dark:from-[#0b1528] dark:to-[#111c2e]',
    dotClass: 'bg-indigo-500',
    barClass: 'bg-gradient-to-r from-indigo-400 to-indigo-600',
  },
  Middle: {
    cardBg: 'border-emerald-100/90 dark:border-emerald-950/60 bg-gradient-to-b from-[#f4fdf8] to-white dark:from-[#062015] dark:to-[#111c2e]',
    dotClass: 'bg-emerald-500',
    barClass: 'bg-gradient-to-r from-emerald-400 to-emerald-600',
  },
  Secondary: {
    cardBg: 'border-blue-100/90 dark:border-blue-950/60 bg-gradient-to-b from-[#f0f9ff] to-white dark:from-[#081f33] dark:to-[#111c2e]',
    dotClass: 'bg-blue-500',
    barClass: 'bg-gradient-to-r from-blue-400 to-blue-600',
  },
  'Higher Secondary': {
    cardBg: 'border-purple-100/90 dark:border-purple-950/60 bg-gradient-to-b from-[#faf5ff] to-white dark:from-[#200e33] dark:to-[#111c2e]',
    dotClass: 'bg-purple-500',
    barClass: 'bg-gradient-to-r from-purple-400 to-purple-600',
  },
};

function SubjectPanel({
  icon: Icon,
  iconBadgeClass = 'bg-[#6366f1]/10 text-[#6366f1]',
  title,
  caption,
  tone,
  subjects,
}: {
  icon: LucideIcon;
  iconBadgeClass?: string;
  title: string;
  caption: string;
  tone: 'emerald' | 'amber' | 'blue';
  subjects: [string, number][];
}) {
  const toneBadgeClass = {
    emerald: 'bg-emerald-50/90 text-emerald-800 border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/60',
    amber: 'bg-amber-50/90 text-amber-800 border-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800/60',
    blue: 'bg-blue-50/90 text-blue-800 border-blue-200/80 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800/60',
  }[tone];

  return (
    <section className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-100 dark:border-slate-800/80 p-5 shadow-xs dark:shadow-none flex flex-col justify-start h-full">
      <header className="flex items-center gap-3 mb-4 min-h-[44px]">
        <div className={cn('h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 shadow-2xs', iconBadgeClass)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-extrabold text-slate-800 dark:text-white leading-tight">{title}</h3>
          <p className="mt-0.5 text-[11.5px] font-medium text-slate-400 dark:text-slate-500">{caption}</p>
        </div>
      </header>

      {subjects.length === 0 ? (
        <p className="text-xs text-slate-400 py-6 text-center">Nothing recorded yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {subjects.map(([subject, count]) => (
            <li
              key={subject}
              className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 dark:border-slate-800/80 bg-slate-50/60 dark:bg-[#0b1322] p-2.5 px-3.5 shadow-2xs hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
            >
              <span className={cn('inline-flex items-center rounded-xl border px-2.5 py-1 text-[12px] font-bold shadow-2xs capitalize', toneBadgeClass)}>
                {subject}
              </span>
              <span className="shrink-0 text-[12px] font-bold tabular-nums text-slate-500 dark:text-slate-400">
                {count} {count === 1 ? 'class' : 'classes'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The ids the figures above the roster scroll to. */
const PANEL_ID: Record<StructureFocus, string> = {
  bands: 'academic-grade-bands',
  subjects: 'academic-subject-catalogue',
};

export interface AcademicStructureTabProps {
  rows: InstituteClass[];
  isLoading?: boolean;
  focus?: StructureFocus | null;
}

export function AcademicStructureTab({
  rows,
  isLoading = false,
  focus = null,
}: AcademicStructureTabProps) {
  const bands = bandsOf(rows);

  useEffect(() => {
    if (!focus || isLoading || rows.length === 0) return;
    document
      .getElementById(PANEL_ID[focus])
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus, isLoading, rows.length]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4.5">
        <Skeleton className="h-64 w-full rounded-3xl" />
        <div className="grid gap-4.5 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-56 w-full rounded-3xl" />
          ))}
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-100 dark:border-slate-800/80 p-8 shadow-sm">
        <EmptyState
          icon={<Layers className="h-6 w-6 text-indigo-500" />}
          title="No structure to show yet"
          description="Once classes are set up, this tab breaks them down by stage and by subject."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section
        id={PANEL_ID.bands}
        className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-100 dark:border-slate-800/80 p-5 shadow-xs dark:shadow-none flex flex-col gap-4 scroll-mt-4"
      >
        <header className="flex items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800/80 min-h-[44px]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-2xl bg-[#6366f1]/10 text-[#6366f1] flex items-center justify-center shrink-0 shadow-2xs">
              <Layers className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-[15.5px] font-extrabold text-slate-800 dark:text-white leading-tight">Grade bands</h2>
              <p className="mt-0.5 text-[11.5px] font-medium text-slate-400 dark:text-slate-500">
                How the institute is banded, youngest first.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1 opacity-20 dark:opacity-10 shrink-0 select-none mr-1" aria-hidden>
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} className="h-1 w-1 rounded-full bg-slate-500" />
            ))}
          </div>
        </header>

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          {bands.map((band) => {
            const config = STAGE_CONFIG[band.stage] ?? STAGE_CONFIG.Primary;
            // How full the band is: its students against its own seats, beside
            // the student count it fills. No seats is an empty bar, not a
            // division by zero, and an over-full band stops at the end.
            const percentage =
              band.seats > 0 ? Math.min(100, Math.round((band.students / band.seats) * 100)) : 0;

            return (
              <li
                key={band.stage}
                className={cn('rounded-2xl border p-4 shadow-2xs flex flex-col justify-between gap-3 h-full', config.cardBg)}
              >
                <div className="flex flex-col gap-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', config.dotClass)} aria-hidden />
                      <span className="truncate text-[14px] font-extrabold text-slate-800 dark:text-slate-100">
                        {band.stage}
                      </span>
                    </div>

                    <span className="font-black text-[12px] text-slate-900 dark:text-white shrink-0">
                      {band.students.toLocaleString('en-IN')} {band.students === 1 ? 'student' : 'students'}
                    </span>
                  </div>

                  {/* Seats filled */}
                  <div
                    className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200/70 dark:bg-slate-800"
                    role="img"
                    aria-label={
                      band.seats > 0
                        ? `${band.students.toLocaleString('en-IN')} of ${band.seats.toLocaleString('en-IN')} seats filled`
                        : 'No seats set'
                    }
                  >
                    <div
                      className={cn('h-full rounded-full transition-all duration-300', config.barClass)}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                    <span>
                      <strong className="font-extrabold text-slate-700 text-sm dark:text-slate-200">{band.classes.length}</strong> {band.classes.length === 1 ? 'class' : 'classes'}
                    </span>
                    <span className="text-slate-500 text-sm dark:text-slate-600">·</span>
                    <span>
                      <strong className="font-extrabold text-slate-700 text-sm dark:text-slate-200">{band.sections}</strong> {band.sections === 1 ? 'section' : 'sections'}
                    </span>
                    <span className="text-slate-500 text-sm dark:text-slate-600">·</span>
                    <span>
                      <strong className="font-extrabold text-slate-700 text-sm dark:text-slate-200">{band.seats.toLocaleString('en-IN')}</strong> seats
                    </span>
                  </div>
                </div>

                {/* Class chips */}
                <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-200/40 dark:border-slate-800/40 mt-auto">
                  {band.classes.map((row) => (
                    <span
                      key={row.uuid}
                      className="inline-flex items-center gap-1 rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] px-2 py-0.5 text-[11.5px] font-bold text-slate-700 dark:text-slate-200 shadow-2xs hover:border-slate-300 transition-colors"
                    >
                      <span>{row.name}</span>
                      <span className="text-slate-500 dark:text-slate-600 font-medium">-</span>
                      <span className="text-[11px] font-extrabold text-slate-500 dark:text-slate-400">
                        {row.sections.length} {row.sections.length === 1 ? 'sec' : 'secs'}
                      </span>
                    </span>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <div id={PANEL_ID.subjects} className="grid gap-6 scroll-mt-4 lg:grid-cols-3">
        <SubjectPanel
          icon={BookOpen}
          iconBadgeClass="bg-[#10b981]/10 text-[#059669]"
          title="Core subjects"
          caption="Taught to everyone in the class"
          tone="emerald"
          subjects={subjectSpread(rows, (row) => row.coreSubjects)}
        />
        <SubjectPanel
          icon={Sparkles}
          iconBadgeClass="bg-[#f59e0b]/10 text-[#d97706]"
          title="Electives"
          caption="Chosen by the student"
          tone="amber"
          subjects={subjectSpread(rows, (row) => row.electives)}
        />
        <SubjectPanel
          icon={Globe2}
          iconBadgeClass="bg-[#3b82f6]/10 text-[#2563eb]"
          title="Languages"
          caption="Offered alongside the medium"
          tone="blue"
          subjects={subjectSpread(rows, (row) => row.languages)}
        />
      </div>
    </div>
  );
}
